<template>
  <div class="web-logs">
    <!-- Modale de détail d'un log -->
    <v-dialog v-model="isDetailOpen" width="90%" max-width="860">
      <modal-detail
        :log="selectedLog"
        :levelColor="levelColor"
        :statutColor="statutColor"
        :typeEvenementColor="typeEvenementColor"
        :formatTs="formatTs"
        @close="closeDetail"
      ></modal-detail>
    </v-dialog>

    <!-- Barre supérieure : titre + KPI compacts + rafraîchir (une seule ligne) -->
    <div class="wl-topbar">
      <div class="wl-head-left">
        <h1 class="wl-title">Logs Web</h1>
        <div class="wl-sub">Suivi de l'activité de vos différents sites web</div>
      </div>
      <template v-if="filters.site">
        <v-chip class="wl-site-chip" color="#5C6BC0" label @click="changeSite">
          <v-icon start size="16">mdi-web</v-icon>
          {{ filters.site }}
          <v-icon end size="16">mdi-close</v-icon>
        </v-chip>
        <div class="wl-kpis">
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#37474F">{{ stats.total }}</span>
            <span class="wl-kpi-label">Total</span>
          </div>
          <!-- Clients actifs sur la période (détail au survol, clic = filtrer sur le client) -->
          <v-menu location="bottom" open-on-hover :open-delay="120" :close-delay="200" :close-on-content-click="false">
            <template #activator="{ props }">
              <div v-bind="props" class="wl-kpi wl-kpi-clients">
                <span class="wl-kpi-value" style="color:#00897B">{{ activeClients.count }}</span>
                <span class="wl-kpi-label">Client{{ activeClients.count > 1 ? "s" : "" }} actif{{ activeClients.count > 1 ? "s" : "" }}</span>
              </div>
            </template>
            <v-card class="wl-clients-card">
              <div class="wl-cl-head">
                <span class="wl-cl-ic"><v-icon size="18">mdi-account-multiple-outline</v-icon></span>
                <div class="wl-cl-title">
                  <b>Clients actifs</b>
                  <span>{{ filters.periode ? periodeLabel(filters.periode) : "Logs chargés" }} · {{ stats.total }} log{{ stats.total > 1 ? "s" : "" }}</span>
                </div>
              </div>
              <div class="wl-cl-stats">
                <div><b>{{ activeClients.count }}</b><span>client{{ activeClients.count > 1 ? "s" : "" }} identifié{{ activeClients.count > 1 ? "s" : "" }}</span></div>
                <div><b>{{ activeClients.sessions }}</b><span>session{{ activeClients.sessions > 1 ? "s" : "" }}</span></div>
                <div><b>{{ activeClients.anonSessions }}</b><span>sans connexion</span></div>
                <div><b>{{ activeClients.ips }}</b><span>adresse{{ activeClients.ips > 1 ? "s" : "" }} IP</span></div>
              </div>
              <div v-if="activeClients.list.length" class="wl-cl-list">
                <button
                  v-for="c in activeClients.list.slice(0, 15)"
                  :key="c.code"
                  type="button"
                  class="wl-cl-row"
                  :title="'Filtrer sur ' + c.label"
                  @click="filters.user = c.label"
                >
                  <span class="wl-cl-av">{{ c.code.slice(0, 2) }}</span>
                  <span class="wl-cl-name">{{ c.label }}</span>
                  <span class="wl-cl-meta">{{ c.count }} log{{ c.count > 1 ? "s" : "" }} · {{ sinceLabel(c.last) }}</span>
                </button>
                <div v-if="activeClients.list.length > 15" class="wl-cl-more">+ {{ activeClients.list.length - 15 }} autre{{ activeClients.list.length - 15 > 1 ? "s" : "" }}</div>
              </div>
              <div v-else class="wl-cl-empty">Aucun client identifié sur la période.</div>
              <div class="wl-cl-foot">Calculé sur les logs chargés (période et filtres en cours). Cliquez sur un client pour filtrer.</div>
            </v-card>
          </v-menu>
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#EF5350">{{ stats.errors }}</span>
            <span class="wl-kpi-label">Erreurs</span>
          </div>
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#FB8C00">{{ stats.warnings }}</span>
            <span class="wl-kpi-label">Alertes</span>
          </div>
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#5C6BC0">{{ stats.avgDuration }}<span class="wl-kpi-unit">ms</span></span>
            <span class="wl-kpi-label">Moyenne</span>
          </div>
        </div>
        <v-btn class="wl-visualiser" variant="flat" color="#5C6BC0" style="color:white" @click="isVizOpen = !isVizOpen">
          <v-icon start>{{ isVizOpen ? 'mdi-table' : 'mdi-chart-timeline-variant' }}</v-icon>
          {{ isVizOpen ? 'Tableau' : 'Visualiser' }}
        </v-btn>
        <v-btn class="wl-refresh" variant="flat" :loading="isLoading" @click="refresh">
          <v-icon start>mdi-refresh</v-icon>
          Rafraîchir
        </v-btn>
      </template>
    </div>


    <!-- Écran de sélection du site (avant tout chargement) -->
    <div v-if="!filters.site" class="wl-gate">
      <v-card class="wl-gate-card" variant="flat">
        <div class="wl-gate-badge">
          <v-icon size="34" color="white">mdi-web</v-icon>
        </div>
        <div class="wl-gate-title">Choisissez un site</div>
        <div class="wl-gate-sub">
          Sélectionnez le site dont vous voulez consulter les logs.
        </div>

        <!-- Chargement des sites -->
        <div v-if="isSitesLoading" class="wl-gate-state">
          <v-progress-circular indeterminate color="#5C6BC0" size="42" width="4"></v-progress-circular>
          <div class="wl-gate-state-text">Récupération des sites…</div>
        </div>

        <!-- Erreur / aucun site -->
        <div v-else-if="sitesError" class="wl-gate-state">
          <v-icon size="40" color="#EF5350">mdi-alert-circle-outline</v-icon>
          <div class="wl-gate-state-text">Impossible de récupérer les sites.</div>
          <v-btn variant="flat" color="#5C6BC0" style="color:white" @click="loadSites">
            <v-icon start>mdi-refresh</v-icon>
            Réessayer
          </v-btn>
        </div>

        <!-- Recherche + chips cliquables : on tape pour filtrer, on clique pour choisir -->
        <div v-else class="wl-gate-picker">
          <v-text-field
            v-model="siteSearch"
            label="Filtrer les sites…"
            variant="outlined"
            density="comfortable"
            hide-details
            clearable
            autofocus
            prepend-inner-icon="mdi-magnify"
            class="wl-gate-search"
          ></v-text-field>
          <div class="wl-gate-chips">
            <button
              v-for="s in filteredSiteList"
              :key="s"
              type="button"
              class="wl-site-chip-btn"
              @click="filters.site = s"
            >
              <v-icon size="15" color="#5C6BC0">mdi-web</v-icon>
              {{ s }}
            </button>
            <div v-if="siteList.length && !filteredSiteList.length" class="wl-gate-state-text">
              Aucun site ne correspond à « {{ siteSearch }} ».
            </div>
            <div v-else-if="!siteList.length" class="wl-gate-state-text">Aucun site disponible.</div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Filtres (recherche + chips) -->
    <v-card v-if="filters.site" class="wl-filters" variant="flat">
      <div class="wl-fbar">
        <v-text-field
          v-model="filters.search"
          label="Rechercher (requête, path, user, message…)"
          variant="outlined" density="compact" hide-details clearable
          prepend-inner-icon="mdi-magnify"
          class="wl-fsearch"
        ></v-text-field>
        <v-btn class="wl-search-btn" variant="flat" color="#5C6BC0" style="color:white" :loading="isLoading" @click="loadLogs">
          <v-icon start>mdi-magnify</v-icon> Rechercher
        </v-btn>
        <v-menu location="bottom start">
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="tonal" color="#5C6BC0" class="wl-addfilter">
              <v-icon start>mdi-plus</v-icon> Filtre
            </v-btn>
          </template>
          <v-list density="compact" class="wl-addlist">
            <v-list-item v-for="f in addableFilters" :key="f.key" @click="editingKey = f.key">
              <template #prepend><v-icon size="18">{{ f.icon }}</v-icon></template>
              <v-list-item-title>{{ f.label }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!addableFilters.length" disabled>
              <v-list-item-title>Tous les filtres sont déjà actifs</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn variant="text" color="#607D8B" @click="resetFilters">
          <v-icon start>mdi-filter-remove-outline</v-icon> Réinitialiser
        </v-btn>
        <v-spacer></v-spacer>
        <div class="wl-live">
          <span v-if="isLive" class="wl-live-dot"></span>
          <v-switch v-model="isLive" color="#43A047" hide-details density="compact" :label="isLive ? 'Live' : 'Live (off)'"></v-switch>
        </div>
      </div>

      <!-- chips des filtres actifs -->
      <div class="wl-fchips" v-if="activeChips.length">
        <button
          v-for="c in activeChips" :key="c.key"
          type="button" class="wl-chip" :class="{ on: editingKey === c.key }"
          @click="editingKey = (editingKey === c.key ? null : c.key)"
        >
          <v-icon size="14">{{ c.icon }}</v-icon>
          <span class="wl-chip-lab">{{ c.label }} :</span>
          <span class="wl-chip-val">{{ c.display }}</span>
          <v-icon size="15" class="wl-chip-x" @click.stop="clearFilter(c.key)">mdi-close</v-icon>
        </button>
      </div>

      <!-- Fenêtre d'édition du filtre sélectionné -->
      <v-dialog :model-value="!!editingKey" @update:model-value="(v) => { if (!v) editingKey = null }" width="460">
        <v-card class="wl-feditor-card">
          <div class="wl-feditor-head">
            <v-icon size="18" start color="white">{{ editingIcon }}</v-icon>
            {{ editingLabel }}
          </div>
          <div class="wl-feditor-body">
            <v-select v-if="editingKey === 'niveau'" v-model="editDraft.niveau" label="Niveau" :items="levelOptions" variant="outlined" density="comfortable" hide-details multiple chips closable-chips></v-select>
            <v-text-field v-else-if="editingKey === 'statut'" v-model="editDraft.statut" label="Statut (ex : 200, 4, 500)" variant="outlined" density="comfortable" hide-details clearable autofocus @keyup.enter="applyEditor"></v-text-field>
            <v-select v-else-if="editingKey === 'typeEvenement'" v-model="editDraft.typeEvenement" label="Type d'événement" :items="typeEvenementOptions" variant="outlined" density="comfortable" hide-details multiple chips closable-chips></v-select>
            <v-select v-else-if="editingKey === 'client'" v-model="editDraft.client" label="Client (IP)" :items="clientOptions" variant="outlined" density="comfortable" hide-details clearable></v-select>
            <v-select v-else-if="editingKey === 'user'" v-model="editDraft.user" label="User" :items="userOptions" variant="outlined" density="comfortable" hide-details clearable></v-select>
            <v-select v-else-if="editingKey === 'parcours'" v-model="editDraft.parcours" label="Session (parcours)" :items="parcoursOptions" variant="outlined" density="comfortable" hide-details clearable :menu-props="{ maxHeight: 320 }"></v-select>
            <template v-else-if="editingKey === 'periode'">
              <v-select v-model="editDraft.periode" label="Période" :items="periodeOptions" item-title="title" item-value="value" variant="outlined" density="comfortable" hide-details clearable :menu-props="{ maxHeight: 360 }"></v-select>
              <div v-if="editDraft.periode === 'custom'" class="wl-feditor-dates">
                <DatePicker v-model="editDraft.dateFrom" :format="'dd/MM/yyyy HH:mm'" :modelType="'timestamp'" locale="fr" :enableTimePicker="true" autoApply placeholder="Début"></DatePicker>
                <DatePicker v-model="editDraft.dateTo" :format="'dd/MM/yyyy HH:mm'" :modelType="'timestamp'" locale="fr" :enableTimePicker="true" autoApply placeholder="Fin"></DatePicker>
              </div>
            </template>
            <v-text-field v-else-if="editingKey === 'limit'" v-model.number="editDraft.limit" type="number" min="1" label="Nb lignes" variant="outlined" density="comfortable" hide-details autofocus @keyup.enter="applyEditor"></v-text-field>
          </div>
          <div class="wl-feditor-foot">
            <v-spacer></v-spacer>
            <v-btn variant="text" color="#607D8B" @click="editingKey = null">Fermer</v-btn>
            <v-btn variant="flat" color="#5C6BC0" style="color:white" @click="applyEditor">
              <v-icon start>mdi-check</v-icon> Appliquer
            </v-btn>
          </div>
        </v-card>
      </v-dialog>
    </v-card>

    <!-- Visualisation : remplace le tableau (le bloc de filtres reste actif et
         impacte la visualisation en direct) -->
    <div v-if="filters.site && isVizOpen" ref="vizCard" class="wl-viz-inline">
      <modal-visualisation
        :logs="filteredLogs"
        :formatTs="formatTs"
        :live="isLive"
        :height="vizHeight"
        @close="isVizOpen = false"
        @open-detail="openDetail"
      ></modal-visualisation>
    </div>

    <!-- Écran de chargement des logs -->
    <div v-if="filters.site && isLoading && !isVizOpen" class="wl-loading">
      <v-progress-circular indeterminate color="#5C6BC0" size="64" width="5"></v-progress-circular>
      <div class="wl-loading-title">Chargement des logs…</div>
      <div class="wl-loading-sub">Site : {{ filters.site }}</div>
    </div>

    <!-- Tableau -->
    <v-card v-if="filters.site && !isLoading && !isVizOpen" ref="tableCard" class="wl-table-card" variant="flat">
      <EasyDataTable
        table-class-name="wl-table"
        :headers="displayHeaders"
        :items="filteredLogs"
        :sort-by="'horodatage'"
        :sort-type="'desc'"
        :rows-per-page="25"
        :table-height="tableHeight"
        :body-row-class-name="rowClass"
        rows-per-page-message="Lignes par page"
        rows-of-page-separator-message="sur"
        empty-message="Aucun log à afficher"
        @click-row="onRowClick"
      >
        <template #item-horodatage="item">
          <span class="wl-cell-date">{{ formatTs(item.horodatage) }}</span>
        </template>

        <template #item-niveau="item">
          <v-chip
            size="small"
            :style="{ backgroundColor: levelColor(item.niveau), color: 'white', fontWeight: 700 }"
          >
            {{ item.niveau || "—" }}
          </v-chip>
        </template>

        <template #item-type_evenement="item">
          <v-chip
            v-if="item.type_evenement"
            size="small"
            label
            :style="{ backgroundColor: typeEvenementColor(item.type_evenement), color: 'white', fontWeight: 700 }"
          >
            {{ item.type_evenement }}
          </v-chip>
          <span v-else>—</span>
        </template>

        <template #item-statut="item">
          <v-chip
            v-if="item.statut"
            size="small"
            :style="{ backgroundColor: statutColor(item.statut), color: 'white', fontWeight: 700 }"
          >
            {{ item.statut }}
          </v-chip>
          <span v-else>—</span>
        </template>

        <template #item-request="item">
          <!-- Toute la cellule ouvre l'aperçu de la requête (près de la souris) -->
          <div class="wl-hz" @mouseenter="hoverIn($event, item)" @mousemove="hoverMove" @mouseleave="hoverOut">
            <div class="wl-req">
              <v-chip
                v-if="requestMethod(item.request)"
                size="x-small"
                label
                :style="{ backgroundColor: methodColor(item.request), color: 'white', fontWeight: 700 }"
              >
                {{ requestMethod(item.request) }}
              </v-chip>
              <span class="wl-cell-mono wl-req-path">{{ requestPath(item.request) || "—" }}</span>
            </div>
          </div>
        </template>

        <template #item-client="item">
          <div class="wl-stack" :title="item.client">
            <span class="wl-stack-main">{{ clientIp(item.client) || "—" }}</span>
            <span class="wl-stack-sub">{{ clientAgentShort(item.client) }}</span>
          </div>
        </template>

        <template #item-user="item">
          <span
            v-if="item.user"
            class="wl-cell-ellipsis wl-clickable"
            :title="'Filtrer sur l\'utilisateur ' + item.user"
            @click.stop="filters.user = userDisplay(item.user)"
          >{{ item.user }}</span>
          <span v-else>—</span>
        </template>

        <template #item-id_parcours="item">
          <span
            v-if="item.id_parcours"
            class="wl-cell-mono wl-session"
            :title="'Filtrer sur la session ' + item.id_parcours"
            @click.stop="filters.parcours = item.id_parcours"
          >{{ item.id_parcours }}</span>
          <span v-else>—</span>
        </template>

        <template #item-duree_ms="item">
          <span v-if="item.duree_ms">{{ item.duree_ms }} ms</span>
          <span v-else>—</span>
        </template>

        <template #item-actions="item">
          <div class="d-flex align-center justify-end">
            <v-btn variant="text" density="compact" size="small" color="#5C6BC0" @click.stop="openDetail(item)">
              <v-icon size="20">mdi-eye-outline</v-icon>
            </v-btn>
            <v-btn variant="text" density="compact" size="small" color="#EF5350" @click.stop="confirmDelete(item)">
              <v-icon size="20">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </EasyDataTable>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top">
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Aperçu d'une requête au survol (une seule carte, à côté du pointeur) -->
    <teleport to="body">
      <div
        v-if="hover.show && hover.item"
        ref="hovercard"
        class="wl-hovercard"
        :style="hover.style"
        @mouseenter="hoverKeep"
        @mouseleave="hoverOut"
        @wheel="onCardWheel"
      >
        <div class="wl-hc-head">
          <v-chip
            v-if="requestMethod(hover.item.request)"
            size="x-small"
            label
            :style="{ backgroundColor: methodColor(hover.item.request), color: 'white', fontWeight: 700 }"
          >{{ requestMethod(hover.item.request) }}</v-chip>
          <v-chip
            v-else-if="hover.item.type_evenement"
            size="x-small"
            label
            :style="{ backgroundColor: typeEvenementColor(hover.item.type_evenement), color: 'white', fontWeight: 700 }"
          >{{ hover.item.type_evenement }}</v-chip>
          <span class="wl-hc-path">{{ requestPath(hover.item.request) || "—" }}</span>
        </div>
        <div class="wl-hc-badges">
          <span v-if="hover.item.statut" class="wl-hc-badge" :style="{ backgroundColor: statutColor(hover.item.statut) }">{{ hover.item.statut }}</span>
          <span v-if="hover.item.niveau" class="wl-hc-badge" :style="{ backgroundColor: levelColor(hover.item.niveau) }">{{ hover.item.niveau }}</span>
          <span v-if="hover.item.duree_ms" class="wl-hc-dur"><v-icon size="13">mdi-timer-outline</v-icon>{{ hover.item.duree_ms }} ms</span>
          <span class="wl-hc-date">{{ formatTs(hover.item.horodatage) }}</span>
        </div>
        <div class="wl-hc-body">
          <div v-if="hover.item.erreur_message" class="wl-hc-error">
            <v-icon size="14">mdi-alert-circle-outline</v-icon><span>{{ hover.item.erreur_message }}</span>
          </div>
          <div class="wl-hc-grid">
            <template v-for="r in hover.rows" :key="r.label">
              <span class="wl-hc-k">{{ r.label }}</span>
              <span class="wl-hc-v" :class="{ mono: r.mono }">{{ r.value }}</span>
            </template>
          </div>
          <template v-if="hover.body">
            <div class="wl-hc-label">Body</div>
            <pre class="wl-hc-pre">{{ hover.body }}</pre>
          </template>
          <template v-if="hover.response">
            <div class="wl-hc-label">Réponse</div>
            <pre class="wl-hc-pre">{{ hover.response }}</pre>
          </template>
        </div>
        <div class="wl-hc-foot"><v-icon size="13">mdi-cursor-default-click-outline</v-icon> Cliquez sur la ligne pour le détail complet</div>
      </div>
    </teleport>
  </div>
</template>

<script>
import homePageData from "../hooks/page-home";
import ModalDetail from "./modal-detail.vue";
import ModalVisualisation from "./modal-visualisation.vue";

export default {
  setup() {
    const {
      logs,
      isLoading,
      isSitesLoading,
      sitesError,
      filters,
      siteList,
      siteOptions,
      clientOptions,
      userOptions,
      typeEvenementOptions,
      parcoursOptions,
      levelOptions,
      selectedLog,
      isDetailOpen,
      autoRefresh,
      isLive,
      newIds,
      snackbar,
      filteredLogs,
      stats,
      activeClients,
      loadLogs,
      refresh,
      resetFilters,
      changeSite,
      openDetail,
      closeDetail,
      deleteLog,
      formatTs,
      levelColor,
      statutColor,
      clientIp,
      clientAgentShort,
      siteHost,
      requestMethod,
      requestPath,
      methodColor,
      typeEvenementColor,
      userDisplay,
      sinceLabel,
    } = homePageData();

    return {
      logs,
      isLoading,
      isSitesLoading,
      sitesError,
      filters,
      siteList,
      siteOptions,
      clientOptions,
      userOptions,
      typeEvenementOptions,
      parcoursOptions,
      levelOptions,
      selectedLog,
      isDetailOpen,
      autoRefresh,
      isLive,
      newIds,
      snackbar,
      filteredLogs,
      stats,
      activeClients,
      loadLogs,
      refresh,
      resetFilters,
      changeSite,
      openDetail,
      closeDetail,
      deleteLog,
      formatTs,
      levelColor,
      statutColor,
      clientIp,
      clientAgentShort,
      siteHost,
      requestMethod,
      requestPath,
      methodColor,
      typeEvenementColor,
      userDisplay,
      sinceLabel,
    };
  },
  data() {
    return {
      // Aperçu au survol de la colonne Requête
      hover: { show: false, item: null, rows: [], body: "", response: "", style: {} },
      isVizOpen: false,
      vizHeight: 500,
      siteSearch: "",
      editingKey: null,
      // Brouillon de la fenêtre d'édition : la sélection n'impacte les filtres
      // qu'au clic sur « Appliquer » (pas en direct).
      editDraft: {
        niveau: [], statut: "", typeEvenement: [], client: null, user: null,
        parcours: null, periode: null, dateFrom: null, dateTo: null, limit: 250,
      },
      periodeOptions: [
        { title: "Dernières 5 min", value: "5m" },
        { title: "Dernières 10 min", value: "10m" },
        { title: "Dernières 15 min", value: "15m" },
        { title: "Dernières 30 min", value: "30m" },
        { title: "Dernière 1 h", value: "1h" },
        { title: "Dernières 2 h", value: "2h" },
        { title: "Dernières 3 h", value: "3h" },
        { title: "Dernières 6 h", value: "6h" },
        { title: "Dernier 1 jour", value: "1d" },
        { title: "Derniers 2 jours", value: "2d" },
        { title: "Derniers 7 jours", value: "7d" },
        { title: "Derniers 14 jours", value: "14d" },
        { title: "Dernier 1 mois", value: "1M" },
        { title: "Derniers 3 mois", value: "3M" },
        { title: "Derniers 6 mois", value: "6M" },
        { title: "Personnalisé", value: "custom" },
      ],
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      tableHeight: 400,
      autoTimer: null,
      headers: [
        { value: "horodatage", text: "Date", sortable: true, width: 150 },
        { value: "niveau", text: "Niveau", sortable: true, width: 92 },
        { value: "type_evenement", text: "Type", sortable: true, width: 96 },
        { value: "statut", text: "Statut", sortable: true, width: 88 },
        { value: "request", text: "Requête", sortable: true, width: 230 },
        { value: "client", text: "Client", sortable: false, width: 185 },
        { value: "user", text: "User", sortable: false, width: 150 },
        { value: "id_parcours", text: "Session", sortable: false, width: 140 },
        { value: "duree_ms", text: "Durée", sortable: true, width: 92 },
        { value: "actions", text: "", sortable: false, width: 84 },
      ],
    };
  },
  computed: {
    isSmallScreen() {
      return this.$vuetify.display.mdAndDown;
    },
    // Sites filtrés par la saisie (les non-correspondants disparaissent).
    filteredSiteList() {
      const q = (this.siteSearch || "").toString().trim().toLowerCase();
      if (!q) return this.siteList;
      return this.siteList.filter((s) => (s || "").toLowerCase().indexOf(q) !== -1);
    },
    // Filtres actifs affichés en chips (cliquer = éditer, ✕ = retirer).
    // Période et Nb lignes sont deux filtres à part entière, toujours affichés.
    activeChips() {
      const f = this.filters;
      const chips = [];
      if (f.periode) {
        chips.push({
          key: "periode",
          label: "Période",
          icon: "mdi-clock-outline",
          display: this.periodeLabel(f.periode),
        });
      }
      chips.push({
        key: "limit",
        label: "Nb lignes",
        icon: "mdi-format-list-numbered",
        display: String(f.limit || ""),
      });
      if (f.niveau && f.niveau.length) {
        chips.push({ key: "niveau", label: "Niveau", icon: "mdi-signal-cellular-outline", display: f.niveau.join(", ") });
      }
      if (f.statut) {
        chips.push({ key: "statut", label: "Statut", icon: "mdi-numeric", display: f.statut });
      }
      if (f.typeEvenement && f.typeEvenement.length) {
        chips.push({ key: "typeEvenement", label: "Type", icon: "mdi-shape-outline", display: f.typeEvenement.join(", ") });
      }
      if (f.client) {
        chips.push({ key: "client", label: "Client", icon: "mdi-ip-network-outline", display: f.client });
      }
      if (f.user) {
        chips.push({ key: "user", label: "User", icon: "mdi-account-outline", display: f.user });
      }
      if (f.parcours) {
        chips.push({ key: "parcours", label: "Session", icon: "mdi-map-marker-path", display: f.parcours });
      }
      return chips;
    },
    // Filtres proposés dans le menu « + Filtre » : ceux qui ne sont pas déjà
    // représentés par une chip active.
    addableFilters() {
      const active = new Set(this.activeChips.map((c) => c.key));
      const defs = [
        { key: "periode", label: "Période", icon: "mdi-clock-outline" },
        { key: "niveau", label: "Niveau", icon: "mdi-signal-cellular-outline" },
        { key: "statut", label: "Statut", icon: "mdi-numeric" },
        { key: "typeEvenement", label: "Type d'événement", icon: "mdi-shape-outline" },
        { key: "client", label: "Client (IP)", icon: "mdi-ip-network-outline" },
        { key: "user", label: "User", icon: "mdi-account-outline" },
        { key: "parcours", label: "Session", icon: "mdi-map-marker-path" },
        { key: "limit", label: "Nb lignes", icon: "mdi-format-list-numbered" },
      ];
      return defs.filter((d) => !active.has(d.key));
    },
    // Libellé / icône du filtre en cours d'édition (titre de la fenêtre).
    editingLabel() {
      const map = {
        niveau: "Niveau", statut: "Statut", typeEvenement: "Type d'événement",
        client: "Client (IP)", user: "User", parcours: "Session",
        periode: "Période", limit: "Nombre de lignes",
      };
      return map[this.editingKey] || "";
    },
    editingIcon() {
      const map = {
        niveau: "mdi-signal-cellular-outline", statut: "mdi-numeric",
        typeEvenement: "mdi-shape-outline", client: "mdi-ip-network-outline",
        user: "mdi-account-outline", parcours: "mdi-map-marker-path",
        periode: "mdi-clock-outline", limit: "mdi-format-list-numbered",
      };
      return map[this.editingKey] || "mdi-filter-variant";
    },
    // Sur petit écran on ne garde que l'essentiel pour éviter le scroll
    // horizontal ; le détail complet reste accessible via l'œil.
    displayHeaders() {
      if (this.isSmallScreen) {
        const keep = ["horodatage", "niveau", "type_evenement", "statut", "request", "actions"];
        return this.headers.filter((h) => keep.includes(h.value));
      }
      return this.headers;
    },
  },
  watch: {
    autoRefresh(val) {
      this.setupAutoRefresh(val);
    },
    isVizOpen(v) {
      if (v) this.computeVizHeight();
    },
    // À l'ouverture de la fenêtre d'édition, on recopie la valeur actuelle du
    // filtre dans le brouillon (la sélection n'impactera les filtres qu'au
    // clic sur « Appliquer »).
    editingKey(key) {
      if (!key) return;
      const f = this.filters;
      this.editDraft = {
        niveau: Array.isArray(f.niveau) ? f.niveau.slice() : [],
        statut: f.statut || "",
        typeEvenement: Array.isArray(f.typeEvenement) ? f.typeEvenement.slice() : [],
        client: f.client != null ? f.client : null,
        user: f.user != null ? f.user : null,
        parcours: f.parcours != null ? f.parcours : null,
        periode: f.periode != null ? f.periode : null,
        dateFrom: f.dateFrom != null ? f.dateFrom : null,
        dateTo: f.dateTo != null ? f.dateTo : null,
        limit: f.limit != null ? f.limit : 250,
      };
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    // L'aperçu est positionné à l'écran : on le ferme dès qu'on fait défiler (sauf l'aperçu lui-même).
    window.addEventListener("scroll", this.onAnyScroll, true);
    this.computeTableHeight();
    // Recalcule après la mise en page finale (polices, filtres qui se replient…)
    setTimeout(this.computeTableHeight, 150);
    setTimeout(this.computeTableHeight, 500);
  },
  updated() {
    // La hauteur des filtres peut changer (chips de niveau, retour à la ligne…)
    this.computeTableHeight();
    this.computeVizHeight();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("scroll", this.onAnyScroll, true);
    clearTimeout(this.hoverTimer);
    if (this.autoTimer) clearInterval(this.autoTimer);
  },
  methods: {
    onRowClick(item) {
      this.hideHover();
      this.openDetail(item);
    },

    // ── Aperçu au survol de « Requête » ────────────────────────────────────
    hoverIn(e, item) {
      clearTimeout(this.hoverTimer);
      const el = e.currentTarget;
      this.hoverMove(e);
      // Aperçu déjà ouvert (ou fermé à l'instant) : on passe d'une ligne à l'autre sans attendre.
      const warm = this.hover.show || Date.now() - (this.hoverClosedAt || 0) < 400;
      this.hoverTimer = setTimeout(() => this.showHover(el, item), warm ? 60 : 120);
    },
    // Dernière position de la souris dans la cellule : l'aperçu s'ouvre à côté du pointeur.
    hoverMove(e) {
      this.hoverMouse = { x: e.clientX, y: e.clientY };
    },
    hoverOut() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(this.hideHover, 150);
    },
    hoverKeep() {
      clearTimeout(this.hoverTimer);
    },
    hideHover() {
      clearTimeout(this.hoverTimer);
      if (this.hover.show) {
        this.hover.show = false;
        this.hoverClosedAt = Date.now();
      }
    },
    // Faire défiler le contenu de l'aperçu (body / réponse) ne doit pas le fermer.
    onAnyScroll(e) {
      const card = this.$refs.hovercard;
      if (card && e && e.target instanceof Node && card.contains(e.target)) return;
      this.hideHover();
      this.hoverClosedAt = 0;
    },
    // Molette sur l'en-tête / le pied d'un aperçu long : fait défiler son contenu, pas la page.
    onCardWheel(e) {
      const card = this.$refs.hovercard;
      const body = card && card.querySelector(".wl-hc-body");
      if (!body || body.contains(e.target) || body.scrollHeight <= body.clientHeight) return;
      e.preventDefault();
      body.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    },
    // JSON indenté et raccourci pour l'aperçu (le détail complet reste dans la fiche).
    prettyShort(value) {
      if (value === undefined || value === null) return "";
      let str = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value).trim();
      if (typeof value !== "object") {
        try { str = JSON.stringify(JSON.parse(str), null, 2); } catch (err) { /* texte brut */ }
      }
      if (!str || str === "{}" || str === "[]" || str === "null" || str === '""') return "";
      const lines = str.split("\n");
      let out = lines.slice(0, 24).join("\n");
      if (out.length > 1800) out = out.slice(0, 1800);
      if (lines.length > 24 || out.length < str.length) out += "\n…";
      return out;
    },
    showHover(el, item) {
      if (!el || !el.isConnected || !item) return;
      const rows = [];
      const add = (label, value, mono) => {
        const v = value === undefined || value === null ? "" : String(value).trim();
        if (v) rows.push({ label: label, value: v, mono: !!mono });
      };
      if (this.requestMethod(item.request)) add("Type", item.type_evenement);
      add("URL", item.path, true);
      add("Site", item.domaine);
      add("User", item.user);
      add("Client", [this.clientIp(item.client), this.clientAgentShort(item.client)].filter(Boolean).join(" · "));
      add("Session", item.id_parcours, true);
      add("Corrélation", item.id_correlation, true);
      add("Code FM", item.code_fm);

      // Position : à côté du pointeur (à droite, sinon à gauche), calée en hauteur
      // sur la souris ; la carte ne suit pas la souris une fois ouverte.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(460, vw - 24);
      const r = el.getBoundingClientRect();
      const m = this.hoverMouse || { x: r.left + 16, y: r.top + r.height / 2 };
      const OFF = 16;
      const maxH = (h) => Math.min(560, Math.max(160, h)) + "px";
      let left = m.x + OFF;
      if (left + width > vw - 12) left = m.x - OFF - width;
      if (left < 12) left = Math.max(12, vw - width - 12);
      const style = { width: width + "px", left: left + "px" };
      if (m.y < vh / 2) {
        const top = Math.max(12, m.y - 18);
        style.top = top + "px";
        style.maxHeight = maxH(vh - top - 12);
      } else {
        const bottom = Math.max(12, vh - m.y - 18);
        style.bottom = bottom + "px";
        style.maxHeight = maxH(vh - bottom - 12);
      }
      this.hover = {
        show: true,
        item: item,
        rows: rows,
        body: this.prettyShort(item.body),
        response: this.prettyShort(item.response),
        style: style,
      };
    },
    onResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
      this.computeTableHeight();
      this.computeVizHeight();
    },
    // Ajuste la hauteur du tableau pour que la carte remplisse l'espace : la
    // marge sous la carte doit être égale à la marge à gauche/droite.
    // Méthode auto-correctrice : on mesure l'écart entre le bas voulu et le bas
    // réel de la carte, puis on ajuste la hauteur d'autant (mapping 1:1).
    computeTableHeight() {
      this.$nextTick(() => {
        var comp = this.$refs.tableCard;
        var el = comp && comp.$el ? comp.$el : comp;
        if (!el || !el.getBoundingClientRect) return;
        var rect = el.getBoundingClientRect();
        var sideGap = Math.round(rect.left); // marge gauche = marge voulue en bas
        var desiredBottom = window.innerHeight - sideGap;
        var delta = Math.floor(desiredBottom - rect.bottom);
        if (Math.abs(delta) <= 1) return; // déjà stable → on arrête (pas de boucle)
        var h = this.tableHeight + delta;
        if (h < 200) h = 200;
        this.tableHeight = h;
      });
    },
    setupAutoRefresh(enabled) {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
      if (enabled) {
        this.autoTimer = setInterval(() => {
          this.refresh();
        }, 30000);
      }
    },
    confirmDelete(item) {
      // Suppression directe (le back confirmera). On garde un garde-fou simple.
      this.deleteLog(item);
    },
    // Changement de période : "custom" attend que l'utilisateur choisisse les
    // dates puis clique Rechercher ; sinon (preset ou vidé) on recharge — le
    // hook calcule la fenêtre glissante à partir du preset.
    applyPeriode(val) {
      if (val === "custom") return;
      this.loadLogs();
    },
    // Valide la fenêtre d'édition : on applique le brouillon aux filtres (donc
    // rien ne bouge tant qu'on n'a pas cliqué « Appliquer »), puis on recharge
    // le serveur uniquement pour les filtres serveur (période / nombre de
    // lignes) ; les autres filtrent déjà en direct. Enfin on ferme la fenêtre.
    applyEditor() {
      const k = this.editingKey;
      const f = this.filters;
      const d = this.editDraft;
      switch (k) {
        case "niveau": f.niveau = Array.isArray(d.niveau) ? d.niveau.slice() : []; break;
        case "statut": f.statut = d.statut; break;
        case "typeEvenement": f.typeEvenement = Array.isArray(d.typeEvenement) ? d.typeEvenement.slice() : []; break;
        case "client": f.client = d.client; break;
        case "user": f.user = d.user; break;
        case "parcours": f.parcours = d.parcours; break;
        case "periode":
          f.periode = d.periode;
          f.dateFrom = d.dateFrom;
          f.dateTo = d.dateTo;
          break;
        case "limit": f.limit = d.limit; break;
        default: break;
      }
      if (k === "periode" || k === "limit") this.loadLogs();
      this.editingKey = null;
    },
    // Libellé lisible d'une valeur de période (pour les chips).
    periodeLabel(val) {
      const o = this.periodeOptions.find((p) => p.value === val);
      return o ? o.title : val;
    },
    // Retire un filtre (remet sa valeur par défaut). Pour la période, on vide la
    // fenêtre glissante et on recharge via le nombre de lignes.
    clearFilter(key) {
      const f = this.filters;
      switch (key) {
        case "niveau": f.niveau = []; break;
        case "statut": f.statut = ""; break;
        case "typeEvenement": f.typeEvenement = []; break;
        case "client": f.client = null; break;
        case "user": f.user = null; break;
        case "parcours": f.parcours = null; break;
        case "periode":
          f.periode = null;
          f.dateFrom = null;
          f.dateTo = null;
          this.loadLogs();
          break;
        case "limit": f.limit = 250; break;
        default: break;
      }
      if (this.editingKey === key) this.editingKey = null;
    },
    // Hauteur de la zone Visualisation : remplit l'espace sous les filtres,
    // avec un bas égal à la marge latérale (comme le tableau).
    computeVizHeight() {
      this.$nextTick(() => {
        const el = this.$refs.vizCard;
        if (!el || !el.getBoundingClientRect) return;
        const rect = el.getBoundingClientRect();
        const sideGap = Math.round(rect.left);
        const desiredBottom = window.innerHeight - sideGap;
        const h = Math.max(300, Math.floor(desiredBottom - rect.top));
        this.vizHeight = h;
      });
    },
    // Classe de ligne : "flash" vert bref sur les lignes qui viennent d'arriver
    // en mode Live.
    rowClass(item) {
      return this.newIds && this.newIds.indexOf(item.id) !== -1 ? "wl-row-new" : "";
    },
  },
  components: {
    ModalDetail,
    ModalVisualisation,
  },
};
</script>

<style>
@import "../style.css";
</style>
